import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import {
  Divider,
  Grid,
  IconButton,
  Input,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import {
  Add as CreateIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Done as SaveIcon,
  Edit as EditIcon,
  History as HistoryIcon,
} from '@mui/icons-material';

import { Table } from '../../components/table/table';
import { CreateFeeModal, DeleteFeeModal, FeeHistoryModal } from './components';
import { HeadCell, Row, RowCell } from '../../components/table/types';

import { UTCToDateTime } from '../../utils/dates';
import { useAppActions } from '../../utils/hooks';
import { FeeDirection, FeeType, Option } from '../../enums';
import { layoutAction } from '../../store/sideBarStore/layoutStore';
import {
  GetFeesDTO,
  PutFeeDTO,
  useDeleteFeeMutation,
  useGetCoinsQuery,
  useGetFeeLevelsQuery,
  useGetFeesQuery,
  usePutFeeMutation,
} from '../../api';
import type { Fee } from '../../types';
import { Link, useParams } from 'react-router-dom';
import { permissionsCheck } from '../../utils/permission';
import { formatAmount } from '../../utils';
import { Filter } from '../../components/filter/filter';
import { FilterButton, FilterField } from '../../components/filter/types';

export const Fees = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { fee_group_id } = useParams<'fee_group_id'>();
  const hasPermissionToEditFees = permissionsCheck(['WRITE_FEES']);
  const [filterValues, setFilterValues] = useState<Partial<GetFeesDTO['params']> | null>(null);

  // api
  const {
    data: feeList,
    isLoading: isFeeLoading,
    refetch,
  } = useGetFeesQuery({
    fee_group_id: fee_group_id ?? '',
    params: filterValues ?? {},
  });
  const [putFee, { isLoading: isFeePutLoading }] = usePutFeeMutation();
  const [deleteFee, { isLoading: isFeeDeleteLoading }] = useDeleteFeeMutation();
  const { data: coins } = useGetCoinsQuery();
  const { data: feeLevels, isLoading: isFeeLevelsLoading } = useGetFeeLevelsQuery({
    fee_group_id: Number(fee_group_id),
  });

  // variables
  const isLoading = isFeeLoading || isFeePutLoading || isFeeDeleteLoading;

  // edit mode logic
  const [activeRowId, setActiveRowId] = useState<number | null>(null);
  const [activeRowLevelId, setActiveRowLevelId] = useState<number | null>(null);
  const [isEditMode, setEditMode] = useState(false);
  const startEditMode = (fee: Fee) => {
    setEditFields(fee as unknown as PutFeeDTO['body']);
    setActiveRowId(fee.id);
    setEditMode(true);
  };
  const offEditMode = () => {
    setEditFields(null);
    setActiveRowId(null);
    setEditMode(false);
  };
  const handleFilterValues = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    setFilterValues((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  useEffect(() => {
    console.log(filterValues);
    refetch();
  }, [filterValues]);

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    hasPermissionToEditFees &&
      setSpeedDialButtonComponent([
        { icon: <CreateIcon />, onClick: openCreateModal, disabled: isEditMode },
      ]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, [isEditMode]);

  // edit fields logic
  const [editFields, setEditFields] = useState<PutFeeDTO['body'] | null>(null);
  const handleEditFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // fee history modal logic
  const [historyModalShown, setHistoryModalShown] = useState(false);
  const openHistoryModal = (fee: Fee) => {
    setHistoryModalShown(true);
    setActiveRowId(fee.id);
    setActiveRowLevelId(fee.fee_level.id);
  };
  const closeHistoryModal = () => {
    setHistoryModalShown(false);
    setActiveRowId(null);
    setActiveRowLevelId(null);
  };

  // add fee modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
    // setEditFields(null);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setEditFields(null);
  };

  // delete fee modal logic
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (fee: Fee) => {
    setDeleteModalShown(true);
    setActiveRowLevelId(fee.fee_level.id);
    setActiveRowId(fee.id);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActiveRowId(null);
  };

  // edit fee handler
  const editFee = async () => {
    if (!activeRowId || !editFields || !fee_group_id) return;

    const args = {
      fee_group_id,
      fee_level_id: editFields.fee_level_id,
      body: {
        coin_id: editFields.coin_id,
        type: editFields.type,
        fixed_amount: String(editFields.fixed_amount),
        percent: String(editFields.percent),
        option: editFields.option,
        fee_level_id: editFields.fee_level_id,
      },
    };

    try {
      await putFee(args).unwrap();
      offEditMode();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // create fee handler
  const createFee = async () => {
    if (!editFields || !fee_group_id) return;

    const args = {
      fee_group_id,
      fee_level_id: editFields.fee_level_id,
      body: {
        coin_id: editFields.coin_id,
        type: editFields.type,
        fixed_amount: String(editFields.fixed_amount),
        percent: String(editFields.percent),
        option: editFields.option,
        fee_level_id: editFields.fee_level_id,
      },
    };

    try {
      await putFee(args).unwrap();
      closeCreateModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // delete fee handler
  const deleteFeeHandler = async () => {
    if (!activeRowId || !fee_group_id || !activeRowLevelId) return;

    const args = { fee_group_id, feeId: activeRowId, fee_level_id: activeRowLevelId };

    try {
      await deleteFee(args).unwrap();
      closeDeleteModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('fees.head-cells.coin') },
    { id: '2', label: t('fees.head-cells.type') },
    { id: '3', label: t('fees.head-cells.fee-level') },
    { id: '3', label: t('fees.head-cells.fixed-amount') },
    { id: '4', label: t('fees.head-cells.option') },
    { id: '5', label: t('fees.head-cells.percent'), align: 'right' },
    { id: '6', label: t('fees.head-cells.row-start') },
    { id: '7', label: t('fees.head-cells.actions'), hidden: !hasPermissionToEditFees },
  ];

  const groupFeesByCoinAndType = (fees: Fee[]) => {
    if (!fees) return {};
    return fees.reduce((acc: any, fee) => {
      const coinKey = fee.coin.name.toLowerCase();

      const rawType = fee.type.toLowerCase();

      if (!acc[coinKey]) {
        acc[coinKey] = {};
      }

      if (!acc[coinKey][rawType]) {
        acc[coinKey][rawType] = [];
      }

      acc[coinKey][rawType].push(fee);

      return acc;
    }, {});
  };
  const grouped = useMemo(() => groupFeesByCoinAndType(feeList?.fees ?? []), [feeList?.fees]);

  const rows: Row[] = useMemo(() => {
    const result: Row[] = [];

    Object.entries(grouped).forEach(([coinCode, types]) => {
      Object.entries(types as Record<string, Fee[]>).forEach(([typeKey, feeArray]) => {
        (feeArray as Fee[])
          .sort((a, b) => a.fee_level.min_turnover - b.fee_level.min_turnover)
          .forEach((fee, index) => {
            const { id, coin, fixed_amount, option, percent, row_start, fee_level } = fee;
            const defaultBorder = '1px solid rgba(224, 224, 224, 1)';
            const cells: RowCell[] = [
              {
                name: 'coin_name',
                value: index === 0 ? coin.name : '',
                sx: {
                  borderBottom:
                    index !== (feeArray as Fee[])?.length - 1 ? 'transparent' : defaultBorder,
                  fontWeight: index === 0 ? 'bold' : 'normal',
                },
              },
              {
                name: 'type',
                value: index === 0 ? t(`common.fee-direction-type.${typeKey.toUpperCase()}`) : '',
                sx: {
                  borderBottom:
                    index !== (feeArray as Fee[])?.length - 1 ? 'transparent' : defaultBorder,
                  fontWeight: index === 0 ? 'bold' : 'normal',
                },
              },
              {
                name: 'fee_level',
                value: `${fee_level.min_turnover} EUR`,
                sx: {
                  borderLeft: '1px solid rgba(224, 224, 224, 1)',
                },
              },
              {
                name: 'fixed_amount',
                value: formatAmount(fixed_amount),
                component:
                  isEditMode && activeRowId === id ? (
                    <Input
                      name="fixed_amount"
                      type="number"
                      value={editFields?.fixed_amount ?? ''}
                      onChange={handleEditFields}
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{ maxWidth: '100px' }}
                      disabled={isFeePutLoading}
                    />
                  ) : undefined,
              },
              {
                name: 'option',
                value: option,
                component:
                  isEditMode && activeRowId === id ? (
                    <Select
                      name="option"
                      value={editFields?.option ?? ''}
                      onChange={handleEditFields}
                      variant="standard"
                      sx={{ width: '80px' }}
                      disabled={isFeePutLoading}
                    >
                      {Object.values(Option).map((it) => (
                        <MenuItem key={it} value={it}>
                          {it}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : undefined,
              },
              {
                name: 'percent',
                value: percent,
                align: 'right',
                formatting: (v: string) => `${v} %`,
                component:
                  isEditMode && activeRowId === id ? (
                    <Input
                      name="percent"
                      type="number"
                      value={editFields?.percent ?? ''}
                      onChange={handleEditFields}
                      inputProps={{ min: 0, step: '0.01' }}
                      endAdornment="%"
                      sx={{ maxWidth: '100px' }}
                      disabled={isFeePutLoading}
                    />
                  ) : undefined,
              },
              {
                name: 'row_start',
                value: row_start,
                formatting: (v: string) => UTCToDateTime(v),
              },
              {
                name: 'actions',
                hidden: !hasPermissionToEditFees,
                component: isEditMode ? (
                  activeRowId === id ? (
                    <Stack direction="row" spacing={2}>
                      <IconButton
                        aria-label="save"
                        color="success"
                        onClick={() => editFee()}
                        disabled={isFeePutLoading}
                      >
                        <SaveIcon />
                      </IconButton>
                      <IconButton
                        aria-label="cancel"
                        color="error"
                        onClick={() => offEditMode()}
                        disabled={isFeePutLoading}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Stack>
                  ) : undefined
                ) : (
                  <Stack direction="row" spacing={2}>
                    <IconButton
                      aria-label="show-history"
                      color="info"
                      onClick={() => openHistoryModal(fee)}
                      disabled={isLoading}
                    >
                      <HistoryIcon />
                    </IconButton>
                    <IconButton
                      aria-label="edit"
                      color="warning"
                      onClick={() => startEditMode(fee)}
                      disabled={isLoading}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label="delete"
                      color="error"
                      onClick={() => openDeleteModal(fee)}
                      disabled={isLoading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                ),
              },
            ];

            result.push({ cells });
          });
      });
    });

    return result;
  }, [grouped, editFields, activeRowId, isEditMode, isLoading, isFeePutLoading]);

  const filterButtons: FilterButton[] = [
    {
      name: 'reset-filter',
      text: t('ui.filter.reset'),
      onClick: () => setFilterValues(null),
    },
  ];

  const sortedFeeLevels = useMemo(() => {
    if (!feeLevels?.feeLevels) return [];
    return [...feeLevels.feeLevels].sort((a, b) => a.min_turnover - b.min_turnover);
  }, [feeLevels?.feeLevels]);

  const filterFields: FilterField[] = [
    {
      name: 'coin_id',
      title: t('fees.filter.coin'),
      component: (
        <Select
          name="coin_id"
          value={filterValues?.coin_id ?? ''}
          onChange={(e) => handleFilterValues(e)}
          variant="standard"
          sx={{ minWidth: '280px', maxWidth: '360px' }}
        >
          {coins?.coins.map((it) => (
            <MenuItem key={it.id} value={it.id}>
              {it.name}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      name: 'rate_markup_group_id',
      title: t('fees.filter.type'),
      component: (
        <Select
          name="type"
          value={filterValues?.type ?? ''}
          onChange={(e) => handleFilterValues(e)}
          variant="standard"
          sx={{ minWidth: '280px', maxWidth: '360px' }}
        >
          {Object.values(FeeType).map((type) => (
            <MenuItem key={type} value={type}>
              {t(`common.fee-direction-type.${type.toUpperCase()}`)}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      name: 'rate_markup_group_id',
      title: t('fees.filter.fee-level'),
      component: (
        <Select
          name="fee_level_id"
          value={filterValues?.fee_level_id ?? ''}
          onChange={(e) => handleFilterValues(e)}
          variant="standard"
          sx={{ minWidth: '280px', maxWidth: '360px' }}
        >
          {sortedFeeLevels.map((it) => (
            <MenuItem key={it.id} value={it.id}>
              {it.min_turnover}
            </MenuItem>
          ))}
        </Select>
      ),
    },
  ];

  return (
    <Stack sx={{ gap: 2 }}>
      <Grid size={{ xs: 12 }}>
        <Filter filterFields={filterFields} filterButtons={filterButtons} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <FeeHistoryModal
        open={historyModalShown}
        onClose={closeHistoryModal}
        feeId={activeRowId}
        feeLevelId={activeRowLevelId}
      />

      <DeleteFeeModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onDelete={deleteFeeHandler}
        isLoading={isFeeDeleteLoading}
      />

      <CreateFeeModal
        open={createModalShown}
        onClose={closeCreateModal}
        onCreate={createFee}
        fields={editFields}
        fieldsHandler={handleEditFields}
        isLoading={isFeePutLoading}
      />
    </Stack>
  );
};
